/**
 * Eine Mail an MEHRERE Ziele hängen (PO-Notiz 26.07.2026, Punkt 11).
 *
 * „Ziel" ist seit Cluster VORGANG (D3) entweder ein Vorgang ODER ein Todo in
 * einem Vorgang — der Picker findet beides, und `MailLink` kann beides tragen
 * (`mail-link.service`).
 *
 * Seit 09/2026 wandert dabei der GANZE VERLAUF mit, nicht nur die geöffnete
 * Nachricht (siehe `mailVerlaufItems.ts`): Eine Zuweisung gilt fachlich dem
 * Schriftwechsel, nicht einer einzelnen Mail daraus.
 *
 * Die Reihenfolge ist hier tragend und der einzige Grund, warum das eine eigene
 * Funktion ist: **erst alle Links schreiben, dann archivieren.**
 *
 * `finishRead()` verschiebt die geöffnete Nachricht ins Archiv, und dabei
 * vergibt Graph eine NEUE messageId. Würde mitten in der Schleife archiviert,
 * zeigten die restlichen Links auf die alte Id — sie liefen ins Leere oder
 * müssten über das messageId-Remapping (`mail-link.service`) geheilt werden.
 *
 * Schlägt ein Link fehl, wird NICHT archiviert: die Mail bliebe sonst
 * unvollständig zugeordnet im Archiv liegen, aus dem Posteingang verschwunden.
 */
export type MailVorgangZiel = { vorgangId?: number; todoId?: number };

export type MailVorgangAssignDeps = {
    /**
     * Verlinkt die Mail samt Verlauf mit EINEM Ziel (POST /mail/links/batch).
     *
     * Rückgabe: der Satz, der dem Nutzer zu sagen wäre — Teilfehler oder eine
     * gekappte Überlänge —, sonst `null`. Ein `throw` bricht wie bisher alles
     * ab, bevor archiviert wird.
     */
    bindVerlauf: (target: MailVorgangZiel) => Promise<string | null>;
    /**
     * Markiert die Mail als gelesen und verschiebt sie ins Archiv.
     *
     * Rueckgabetyp bewusst `unknown`: die Implementierung im MailDetailWindow
     * liefert ein Erfolgs-Flag (false = Archivieren fehlgeschlagen, Fenster
     * bleibt mit Fehlermeldung offen). Hier wird es NICHT ausgewertet — die
     * Links sind zu dem Zeitpunkt bereits geschrieben.
     */
    finishRead: () => Promise<unknown>;
    /**
     * Nimmt die gesammelte Meldung entgegen (eine je Zuweisung, nicht eine je
     * Ziel — bei zwei Vorgängen mit demselben Problem läse der Nutzer sonst
     * denselben Satz zweimal).
     */
    melde?: (text: string) => void;
    /**
     * T-236: läuft NACH dem Schreiben aller Links und VOR dem Archivieren —
     * der Ort für alles, was noch die alte messageId braucht (Bilder aus dem
     * Mailtext per Graph-Import in die Ziele übernehmen). Ein Fehler hier
     * hält die Zuordnung nicht auf: die Links stehen schon.
     */
    vorArchivieren?: (ziele: MailVorgangZiel[]) => Promise<void>;
};

export async function assignMailToZiele(
    ziele: MailVorgangZiel[],
    deps: MailVorgangAssignDeps,
): Promise<void> {
    const meldungen: string[] = [];
    for (const ziel of ziele) {
        // Leere Ziele überspringen — sonst entstünde ein MailLink ohne Bezug.
        if (ziel.vorgangId == null && ziel.todoId == null) continue;
        const meldung = await deps.bindVerlauf(ziel);
        if (meldung && !meldungen.includes(meldung)) meldungen.push(meldung);
    }
    if (meldungen.length > 0) deps.melde?.(meldungen.join(" "));
    if (deps.vorArchivieren) {
        const gebunden = ziele.filter((z) => z.vorgangId != null || z.todoId != null);
        try {
            await deps.vorArchivieren(gebunden);
        } catch {
            /* der Aufrufer meldet selbst; archiviert wird trotzdem */
        }
    }
    await deps.finishRead();
}
